// Every string AnnouncementToast renders of its own, and the shape a caller
// may replace them with.
//
// The mechanism is BidCard's exactly — one object of finished text in one
// language, merged over these defaults key by key, with an UNKNOWN key rejected
// because that is the failure that renders English forever with nothing saying
// why. The reasoning is written out once, in bidCardTranslations.js.
//
// The toast's words of its own are the way its title names a lane, the "now"
// beside the title, and the word at its end that says what a press does. Who is
// speaking, what they said and the lane arrive from the page already in the
// reader's language.
//
// Deliberately NOT exported from the package barrel: the catalog gate holds that
// a barrel exports components and nothing else.

import { unreadTranslationKeys as unreadKeysAgainst } from './bidCardTranslations';

/**
 * The English defaults, frozen.
 *
 * `{name}` placeholders are filled by the toast, with BidCard's `interpolate`.
 */
export const ANNOUNCEMENT_TOAST_TRANSLATIONS = Object.freeze({
  // The title when the page names a lane: who is speaking, and the lane the
  // desk is speaking about. The separator is part of the string, as BidCard's
  // `proxyWide` and SaleDeskStrip's eyebrow have it, because where it goes is
  // the translation's decision. With no lane the title is the page's words
  // alone.
  heading: '{title} · {lane}',

  // Beside the title. A toast is raised the moment its announcement arrives
  // and is gone six seconds later, so it is always now.
  now: 'now',

  // The word at the toast's end, which says what a press does: the desk's
  // notices open to be read, and a person's message opens to be answered.
  view: 'View',
  reply: 'Reply',
});

/**
 * The keys in `translations` that AnnouncementToast would not read, as dotted
 * paths.
 *
 * The walk itself is BidCard's, which takes the defaults object as an argument,
 * so this surface passes its own rather than owning a second copy of a
 * comparison that has to behave identically everywhere.
 */
export const unreadTranslationKeys = (translations) =>
  unreadKeysAgainst(translations, ANNOUNCEMENT_TOAST_TRANSLATIONS);

/** One language's text over the English defaults, key by key. */
export function mergeTranslations(translations) {
  if (!translations) {
    return ANNOUNCEMENT_TOAST_TRANSLATIONS;
  }

  return { ...ANNOUNCEMENT_TOAST_TRANSLATIONS, ...translations };
}

/**
 * The `i18n` prop's validator: `{ translations }`, optional.
 *
 * Closed on the outer object too — `translation` for `translations` fails
 * exactly the way a mis-keyed string does, silently and in English.
 */
export function isValidAnnouncementToastI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') {
    return false;
  }
  if (Object.keys(i18n).some((key) => key !== 'translations')) {
    return false;
  }
  if (i18n.translations === undefined) {
    return true;
  }
  return unreadTranslationKeys(i18n.translations).length === 0;
}
